import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  ControlGroup,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  Icon,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";
import moment from "moment-timezone";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { apiCallPost } from "../../../micro/fns/util";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { withRouter } from "react-router-dom";

class MRBillRegister extends React.Component {
  state = {
    // Data
    rows: [],
    allRows: [], // All rows data for client-side filtering
    branches: [],
    doctors: [],
    users: [],

    // Loading states
    isLoading: false,
    isGenerating: false,

    // Filters
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "",
    selectedDoctor: "",
    selectedBranchName: null,
    selectedDoctorName: null,
    selectedMop: "",
    selectedBilledBy: "",
    // Grid
    gridApi: null,
    columnApi: null,
  };

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  getDisplayedColumns = () => {
    try {
      const { columnApi } = this.state;
      if (columnApi && columnApi.getAllDisplayedColumns) {
        return columnApi.getAllDisplayedColumns().map((col) => {
          const def = col.getColDef();
          return {
            headerName: def.headerName || def.field || col.getColId(),
            field: def.field || col.getColId(),
          };
        });
      }
    } catch (e) {}
    // Fallback to default column defs if columnApi not ready
    return this.getColumnDefs()
      .filter((d) => !!d.field)
      .map((d) => ({ headerName: d.headerName, field: d.field }));
  };

  // Navigate to surgery details page
  navigateToDetails = (surgery) => {
    const detailsPath = `/v3/workflow/surgery-booking/${surgery._id || surgery.bookingReference}`;
    window.open(detailsPath, "_blank", "noopener,noreferrer");
  };

  getColumnDefs = () => {
    return [
      { headerName: "S No", field: "serial", width: 90, pinned: "left" },
      { headerName: "Bill No", field: "billNo", width: 140 },
      {
        headerName: "Booking Ref",
        field: "bookingReference",
        width: 160,
        cellRenderer: (params) => {
          const value = params.value;
          if (!value || value === "-") return "-";

          const container = document.createElement("div");
          const link = document.createElement("span");

          link.innerHTML = value;
          link.style.color = "#137cbd";
          link.style.cursor = "pointer";
          link.style.textDecoration = "underline";
          link.style.fontWeight = "600";
          link.onclick = () => this.navigateToDetails(params.data);

          container.appendChild(link);
          return container;
        },
      },
      { headerName: "PID", field: "pid", width: 140 },
      { headerName: "P. Name", field: "patientName", width: 220 },
      { headerName: "D. Name", field: "doctorName", width: 220 },
      { headerName: "Branch", field: "branchName", width: 180 },
      {
        headerName: "SX Date",
        field: "sxDate",
        width: 140,
        valueFormatter: (p) => this.formatSxDate(p.value),
        comparator: (a, b) => {
          const dayA = this.toIstDay(a) || "";
          const dayB = this.toIstDay(b) || "";
          return dayA.localeCompare(dayB);
        },
      },
      { headerName: "Type", field: "type", width: 120 },
      { headerName: "M.O.P", field: "mop", width: 120 },
      {
        headerName: "Amount",
        field: "netBill",
        width: 140,
        valueFormatter: (p) => (p.value == null ? "-" : this.formatCurrency(Number(p.value))),
        cellStyle: { textAlign: "left" },
      },
      { headerName: "Billed By", field: "billedBy", width: 160 },
      {
        headerName: "Billed At",
        field: "billedAt",
        width: 190,
        valueFormatter: (p) => this.formatBilledAtIst(p.value),
      },
      { headerName: "Payment Remarks", field: "paymentRemarks", width: 240 },
    ];
  };

  // Surgery date can arrive under several names depending on how the booking record is flattened.
  // Falls back to the payment timestamp (ts) so the "Date" column is never blank.
  extractSxDate = (raw) => {
    if (!raw) return null;
    return (
      raw.sxDate ||
      raw.surgeryDate ||
      raw.scheduledDate ||
      raw.sxScheduledDate ||
      raw.dateOfSurgery ||
      raw.booking?.scheduledDate ||
      raw.bookingDetails?.scheduledDate ||
      raw.ts ||          // payment event timestamp — always present
      raw.billedAt ||
      raw.createdAt ||
      null
    );
  };

  // Day (in IST) a value belongs to, as YYYY-MM-DD; null when unparseable
  toIstDay = (value) => {
    if (!value) return null;
    const date = moment.utc(value).tz("Asia/Kolkata");
    return date.isValid() ? date.format("YYYY-MM-DD") : null;
  };

  // A transaction is "past dated" when it was collected on a day later than its surgery date,
  // i.e. the payment/refund was taken against a previous-date Surgery ID.
  // Compared row-wise (not against the filter dates) so it holds for any date range.
  isPastDatedSx = (row) => {
    const sxDay = this.toIstDay(row?.sxDate);
    if (!sxDay) return false;
    const billedDay = this.toIstDay(row?.billedAt) || moment(this.state.fromDate).format("YYYY-MM-DD");
    return sxDay < billedDay;
  };

  // Keeps current-date surgery transactions in their normal sequence and pushes
  // previous-date surgery transactions to the bottom, then renumbers S No
  arrangeRowsBySxDate = (rows) => {
    const currentRows = [];
    const pastRows = [];
    (rows || []).forEach((row) => {
      (this.isPastDatedSx(row) ? pastRows : currentRows).push(row);
    });

    return [...currentRows, ...pastRows].map((row, idx) => ({
      ...row,
      serial: idx + 1,
      isPastDatedSx: this.isPastDatedSx(row),
    }));
  };

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });
      const branches = await this.loadBranches();
      await Promise.all([this.loadDoctors(), this.loadUsers()]);
      // Default branch from global selection if available
      if (Array.isArray(branches) && branches.length) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();
        const match = preferredName ? branches.find((b) => norm(b.name) === norm(preferredName)) : null;
        if (match)
          this.setState({
            selectedBranch: match._id,
            selectedBranchName: match.name,
          });
      }
      await this.generateReport();
    } catch (err) {
      console.error("Error loading initial data:", err);
      this.loadDummyData();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  // Load branches like Surgery Appointment Register
  loadBranches = async () => {
    try {
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});

      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      this.setState({ branches });
      return branches;
    } catch (err) {
      // Fallback endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`, {});
        const rawBranches = response?.data || response || [];
        const branches = (Array.isArray(rawBranches) ? rawBranches : [])
          .map((site, idx) => ({
            _id: site?._id || site?.id || String(idx),
            name: site?.site || site?.siteName || site?.name || site?.branchName || site?.label || `Site ${idx + 1}`,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ branches });
        return branches;
      } catch (fallbackErr) {
        // Final fallback: small static list
        const fallback = [
          { _id: "branch1", name: "Barrackpore" },
          { _id: "branch2", name: "Kolkata" },
          { _id: "branch3", name: "Howrah" },
        ];
        this.setState({ branches: fallback });
        return fallback;
      }
    }
  };

  loadDoctors = async () => {
    try {
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});
      const doctors = Array.isArray(response)
        ? response.map((consultant) => ({
            _id: consultant._id || consultant.id,
            name: consultant.name || consultant.consultantName,
            specialization: consultant.specialization || consultant.department,
            phone: consultant.phone || consultant.contactNumber,
            email: consultant.email || consultant.emailId,
            shortcode: consultant.shortcode || consultant.code,
          }))
        : [];
      this.setState({ doctors });
      return doctors;
    } catch (err) {
      // Fallback endpoint
      try {
        const response = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`, {});
        const doctors = response?.data || response || [];
        this.setState({ doctors });
        return doctors;
      } catch (fallbackErr) {
        // Final fallback static list
        const fallback = [
          { _id: "doc1", name: "DR. ARIJIT MITRA" },
          { _id: "doc2", name: "DR. ARNAB DAS" },
          { _id: "doc3", name: "DR. SUDIPTA ROY" },
          { _id: "doc4", name: "DR. PRITAM SARKAR" },
        ];
        this.setState({ doctors: fallback });
        return fallback;
      }
    }
  };

  loadUsers = async () => {
    const fallback = [
      { _id: "usr1", name: "Front Desk 1" },
      { _id: "usr2", name: "Front Desk 2" },
      { _id: "usr3", name: "Accounts" },
    ];
    this.setState({ users: fallback });
  };

  generateReport = async () => {
    try {
      this.setState({ isGenerating: true });
      cogoToast.loading("Generating MR Bill Register...");

      const { fromDate, toDate, selectedBranch, selectedDoctor, selectedMop, selectedBilledBy, branches, doctors } =
        this.state;
      const prefix = this.props.authStore.prefix;

      const selectedBranchName = selectedBranch ? branches.find((b) => b._id === selectedBranch)?.name : undefined;
      const selectedDoctorName = selectedDoctor ? doctors.find((d) => d._id === selectedDoctor)?.name : undefined;

      const payload = {
        // Date range filters - standardized format like IPDBillRegister
        startDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
        endDate: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
        // Branch filters (both id and name if available)
        branchId: selectedBranch || undefined,
        branchName: selectedBranchName || undefined,
        // Note: Doctor filtering is now handled client-side for better UX
        // Payment mode and user
        mode: selectedMop || undefined,
        doneBy: selectedBilledBy || undefined,
      };

      // POST with JSON payload only (no GET fallback)
      let response = await apiCallPost(`/v3/${prefix}/neoSxBookingMaster/mrBillRegister`, payload);

      const extractItems = (resp) => {
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp?.data)) return resp.data;
        if (Array.isArray(resp?.data?.items)) return resp.data.items;
        if (Array.isArray(resp?.items)) return resp.items;
        if (Array.isArray(resp?.records)) return resp.records;
        return [];
      };
      let data = extractItems(response);

      const rows = (Array.isArray(data) ? data : []).map((r, idx) => ({
        _id: r._id || r.id || `mr_${idx}`,
        serial: idx + 1,
        billNo: r.moneyReceiptNo || r.billNo || r.billNumber || r.billNoStr || r.billno || "-",
        bookingReference: r.bookingReference || r.bookingId || "-",
        pid: r.patientId || r.pid || r.patientPID || r.patientPid || "-",
        doctorName: r.doctorName || r.doctor || "-",
        patientName: r.patientName || r.name || "-",
        sxDate: this.extractSxDate(r),
        type: r.type || r.trxType || "",
        mop: r.mode || r.mop || r.modeOfPayment || r.paymentMode || "-",
        billedBy: r.doneBy || r.billedBy || r.user || r.createdByName || "-",
        paymentRemarks: r.paymentRemarks || r.remarks || "",
        netBill: r.amount ?? r.netBill ?? r.netAmount ?? r.net ?? r.totalNet ?? 0,
        billedAt: r.ts || r.billedAt || r.billDate || r.createdAt || null,
        branchId: r.branchId || r.branch?._id,
        branchName: r.branchName || r.branch?.name,
        doctorId: r.doctorId || r.doctor?._id,
      }));

      if (rows.length && !rows.some((r) => !!r.sxDate)) {
        console.warn(
          "MR Bill Register: no surgery date found on any row. Available keys on first record:",
          Object.keys(data[0] || {})
        );
      }

      // Derive "Billed By" list from payload response
      const billedBySet = new Set(rows.map((r) => r.billedBy).filter((x) => !!x && x !== "-"));
      const users = Array.from(billedBySet)
        .sort()
        .map((name, i) => ({ _id: `u_${i}`, name }));

      // Store all rows for client-side filtering
      this.setState(
        {
          allRows: rows,
          users: users.length ? users : this.state.users,
        },
        () => {
          // Apply client-side filters after storing all data
          this.applyClientSideFilters();
        }
      );
    } catch (err) {
      console.error("Failed generating report", err);
      cogoToast.error("Failed to generate report");
      this.loadDummyData();
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  loadDummyData = () => {
    const { fromDate, toDate, selectedBranch, selectedDoctor, selectedMop, selectedBilledBy } = this.state;

    const mops = ["Cash", "Card", "UPI", "NEFT", "Wallet", "Other"];
    const billedByList = ["Front Desk 1", "Front Desk 2", "Accounts"];
    const doctors = ["DR. ARIJIT MITRA", "DR. ARNAB DAS", "DR. SUDIPTA ROY"];
    const branches = [
      { _id: "branch1", name: "Barrackpore" },
      { _id: "branch2", name: "Kolkata" },
      { _id: "branch3", name: "Howrah" },
    ];

    const start = moment(fromDate).startOf("day");
    const end = moment(toDate).endOf("day");
    const days = end.diff(start, "days") + 1;

    const rows = Array.from({ length: 500 }).map((_, i) => {
      const dt = start
        .clone()
        .add(i % Math.max(days, 1), "days")
        .toDate();
      const mop = mops[i % mops.length];
      const bb = billedByList[i % billedByList.length];
      const doc = doctors[i % doctors.length];
      const br = branches[i % branches.length];
      return {
        _id: `dummy_mr_${i}`,
        serial: i + 1,
        billNo: `MR-${(10000 + i).toString()}`,
        pid: `P${(401405500 + i).toString()}`,
        doctorName: doc,
        patientName: i % 2 === 0 ? "JOHN DOE" : `Patient ${i + 1}`,
        // Every 7th transaction is taken against a previous-date surgery
        sxDate:
          i % 7 === 0
            ? moment(dt)
                .subtract((i % 3) + 1, "days")
                .toDate()
            : dt,
        type: i % 5 === 0 ? "Patient Refund" : "Payment",
        mop,
        billedBy: bb,
        netBill: 500 + (i % 8) * 125,
        branchId: br._id,
        branchName: br.name,
        doctorId: `doc${(i % doctors.length) + 1}`,
        billedAt: dt,
      };
    });

    const filtered = rows.filter((r) => {
      // Use same date format as API payload - compare dates only, not times
      const billDate = moment(r.billedAt).format("YYYY-MM-DD");
      const startDateStr = moment(fromDate).format("YYYY-MM-DD");
      const endDateStr = moment(toDate).format("YYYY-MM-DD");
      const dateOk = billDate >= startDateStr && billDate <= endDateStr;

      const brOk = !selectedBranch || r.branchId === selectedBranch;
      const docOk = !selectedDoctor || r.doctorId === selectedDoctor;
      const mopOk = !selectedMop || r.mop === selectedMop;
      const bbOk = !selectedBilledBy || r.billedBy === selectedBilledBy;
      return dateOk && brOk && docOk && mopOk && bbOk;
    });

    this.setState(
      {
        allRows: filtered,
      },
      () => {
        // Apply client-side filters after storing dummy data
        this.applyClientSideFilters();
      }
    );
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value }, () => {
      // Apply client-side filtering for MOP and Billed By changes if we have data
      if ((field === "selectedMop" || field === "selectedBilledBy") && this.state.allRows.length > 0) {
        this.applyClientSideFilters();
      }
    });
  };

  // Helper function to create branch items for dropdown
  createBranchSelectItems = () => {
    return this.state.branches.map((branch) => branch.name).filter(Boolean);
  };

  // Helper function to create doctor items for dropdown with shortcode formatting
  createDoctorSelectItems = () => {
    return this.state.doctors
      .map((doctor) => {
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        const name = doctor.name;
        return `${shortcode}${name}`;
      })
      .filter(Boolean);
  };

  // Helper function to get formatted doctor name
  getFormattedDoctorName = (doctor) => {
    if (!doctor) return null;
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
  };

  // Helper function to extract doctor name from formatted string
  extractDoctorNameFromFormatted = (formattedName) => {
    if (!formattedName) return null;
    // Remove shortcode part (everything before " | ")
    const withoutShortcode = formattedName.includes(" | ") ? formattedName.split(" | ")[1] : formattedName;
    return withoutShortcode.trim();
  };

  // Apply client-side filtering to existing data
  applyClientSideFilters = () => {
    let filteredRows = [...this.state.allRows];

    // Apply doctor filter if selected
    if (this.state.selectedDoctor && this.state.selectedDoctorName) {
      const extractedDoctorName = this.extractDoctorNameFromFormatted(this.state.selectedDoctorName);
      console.log("🔍 DEBUG: Applying client-side doctor filter:", extractedDoctorName);

      filteredRows = filteredRows.filter((row) => {
        const rowDoctorName = row.doctorName || "";
        // Try multiple matching strategies
        const matches = [
          // Exact match
          rowDoctorName === extractedDoctorName,
          // Match without "DR. " prefix
          rowDoctorName.replace(/^DR\.\s*/i, "") === extractedDoctorName.replace(/^DR\.\s*/i, ""),
          // Case-insensitive match
          rowDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
          // Partial match (contains)
          rowDoctorName.toLowerCase().includes(extractedDoctorName.toLowerCase()),
          // Also check doctorId match
          row.doctorId === this.state.selectedDoctor,
        ].some(Boolean);

        return matches;
      });

      console.log("🔍 DEBUG: Client-side filtering result:", {
        original: this.state.allRows.length,
        filtered: filteredRows.length,
        extractedDoctorName,
      });
    }

    // Apply MOP filter if selected
    if (this.state.selectedMop) {
      filteredRows = filteredRows.filter((row) => {
        const rowMop = row.mop || "";
        return rowMop === this.state.selectedMop;
      });
      console.log("🔍 DEBUG: Applied MOP filter:", this.state.selectedMop, "Remaining rows:", filteredRows.length);
    }

    // Apply Billed By filter if selected
    if (this.state.selectedBilledBy) {
      filteredRows = filteredRows.filter((row) => {
        const rowBilledBy = row.billedBy || "";
        return rowBilledBy === this.state.selectedBilledBy;
      });
      console.log(
        "🔍 DEBUG: Applied Billed By filter:",
        this.state.selectedBilledBy,
        "Remaining rows:",
        filteredRows.length
      );
    }

    // Update state with filtered data, previous-date surgery transactions pushed to the bottom
    this.setState({ rows: this.arrangeRowsBySxDate(filteredRows) });

    // Show feedback to user with all active filters
    const activeFilters = [];
    if (this.state.selectedDoctorName) {
      activeFilters.push(`Doctor: ${this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)}`);
    }
    if (this.state.selectedMop) {
      activeFilters.push(`MOP: ${this.state.selectedMop}`);
    }
    if (this.state.selectedBilledBy) {
      activeFilters.push(`Billed By: ${this.state.selectedBilledBy}`);
    }

    const filterMsg =
      activeFilters.length > 0
        ? `Filtered to ${filteredRows.length} records (${activeFilters.join(", ")})`
        : `Showing all ${filteredRows.length} records`;

    cogoToast.success(filterMsg);
  };

  // Handle branch selection from SingleSelectWrapper
  handleBranchFilterSelect = (branchName) => {
    const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
    this.setState({
      selectedBranch: selectedBranch ? selectedBranch._id : "",
      selectedBranchName: branchName || null,
    });
  };

  // Handle doctor selection from SingleSelectWrapper
  handleDoctorFilterSelect = (formattedDoctorName) => {
    // Find the doctor by matching the formatted name
    const selectedDoctor = this.state.doctors.find((doctor) => {
      const formatted = this.getFormattedDoctorName(doctor);
      return formatted === formattedDoctorName;
    });

    this.setState(
      {
        selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
        selectedDoctorName: formattedDoctorName || null,
      },
      () => {
        // Apply filtering immediately if we have data
        if (this.state.allRows.length > 0) {
          this.applyClientSideFilters();
        }
      }
    );
  };

  formatCurrency = (num) => {
    if (num === null || typeof num === "undefined") return "-";
    try {
      return num.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      });
    } catch (e) {
      return `${num}`;
    }
  };

  formatSxDate = (value) => {
    const day = this.toIstDay(value);
    return day ? moment(day, "YYYY-MM-DD").format("DD-MMM-YYYY") : "-";
  };

  formatBilledAtIst = (value) => {
    if (!value) return "-";
    try {
      const date = moment.utc(value).tz("Asia/Kolkata");
      return date.isValid() ? date.format("DD-MMM-YYYY HH:mm") : "-";
    } catch (err) {
      return "-";
    }
  };

  getReportGeneratedAtIst = () => {
    return `${moment().tz("Asia/Kolkata").format("DD-MMM-YYYY HH:mm")} IST`;
  };

  exportToCSV = () => {
    try {
      // Get filtered data from AG Grid
      let filteredData = [];
      if (this.state.gridApi) {
        // Get all filtered rows from AG Grid
        this.state.gridApi.forEachNodeAfterFilter((node) => {
          filteredData.push(node.data);
        });
      } else {
        // Fallback to state data if grid API not available
        filteredData = this.state.rows;
      }

      const { branches, doctors, fromDate, toDate, selectedBranch, selectedDoctor, selectedMop, selectedBilledBy } =
        this.state;

      const reportGeneratedAt = this.getReportGeneratedAtIst();
      const selectedBranchName = selectedBranch ? branches.find((b) => b._id === selectedBranch)?.name || "-" : "All";
      const selectedDoctorName = selectedDoctor ? doctors.find((d) => d._id === selectedDoctor)?.name || "-" : "All";

      const headerInfo = [
        ["MR Bill Register"],
        [`Date: ${moment(fromDate).format("DD-MMM-YYYY")} to ${moment(toDate).format("DD-MMM-YYYY")}`],
        [
          `Branch: ${selectedBranchName}`,
          `Doctor: ${selectedDoctorName}`,
          `MOP: ${selectedMop || "All"}`,
          `Billed By: ${selectedBilledBy || "All"}`,
        ],
        [`Report Generated At: ${reportGeneratedAt}`],
        [],
      ];

      const visibleCols = this.getDisplayedColumns();
      const headers = [visibleCols.map((c) => c.headerName)];

      const orderedData = this.arrangeRowsBySxDate(filteredData);
      const firstPastDatedIndex = orderedData.findIndex((r) => this.isPastDatedSx(r));

      const dataRows = orderedData.reduce((acc, r, idx) => {
        if (idx === firstPastDatedIndex) {
          acc.push(["Payments / refunds recorded against previous-date Surgery (SX) dates"]);
        }
        acc.push(
          visibleCols.map((c) => {
            const v = r[c.field];
            if (c.field === "netBill") return Number(v) || 0;
            if (c.field === "billedAt") return this.formatBilledAtIst(v);
            if (c.field === "sxDate") return this.formatSxDate(v);
            return v == null ? "" : v;
          })
        );
        return acc;
      }, []);

      const csvData = [...headerInfo, ...headers, ...dataRows];
      const csvContent = csvData
        .map((row) =>
          row
            .map((cell) => {
              if (cell === null || typeof cell === "undefined") return "";
              const str = String(cell).replace(/"/g, '""');
              return /[",\n]/.test(str) ? `"${str}"` : str;
            })
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const fileName = `MR_Bill_Register_${moment(fromDate).format("DD-MMM-YYYY")}_to_${moment(toDate).format(
        "DD-MMM-YYYY"
      )}_Generated_${moment().format("DD-MMM-YYYY_HH-mm")}.csv`;
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      cogoToast.success(`CSV exported successfully: ${fileName}`);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      cogoToast.error("Failed to export CSV");
    }
  };

  exportToPDF = () => {
    try {
      // Get filtered data from AG Grid
      let filteredData = [];
      if (this.state.gridApi) {
        // Get all filtered rows from AG Grid
        this.state.gridApi.forEachNodeAfterFilter((node) => {
          filteredData.push(node.data);
        });
      } else {
        // Fallback to state data if grid API not available
        filteredData = this.state.rows;
      }

      const {
        branches,
        doctors,
        fromDate,
        toDate,
        selectedBranch,
        selectedDoctor,
        selectedMop,
        selectedBilledBy,
        selectedDoctorName,
      } = this.state;
      const visibleCols = this.getDisplayedColumns();

      const reportGeneratedAt = this.getReportGeneratedAtIst();
      const selectedBranchName = selectedBranch ? branches.find((b) => b._id === selectedBranch)?.name || "-" : "All";
      const displayDoctorName = selectedDoctorName ? this.extractDoctorNameFromFormatted(selectedDoctorName) : "All";

      const paymentsForPdf = (filteredData || []).filter((r) => {
        const typeStr = String(r.type || "").toLowerCase();
        return typeStr === "payment";
      });

      const refundsForPdf = (filteredData || []).filter((r) => {
        const typeStr = String(r.type || "").toLowerCase();
        return typeStr === "refund";
      });

      const paymentsTotal = paymentsForPdf.reduce((sum, r) => sum + Math.abs(Number(r.netBill) || 0), 0);
      const refundsTotal = refundsForPdf.reduce((sum, r) => sum + Math.abs(Number(r.netBill) || 0), 0);
      const total = paymentsTotal - refundsTotal;

      const printWindow = window.open("", "_blank");
      const orderedData = this.arrangeRowsBySxDate(filteredData);
      const firstPastDatedIndex = orderedData.findIndex((r) => this.isPastDatedSx(r));
      const separatorRow = `<tr class="sx-separator"><td colspan="${visibleCols.length}">Payments / refunds recorded against previous-date Surgery (SX) dates</td></tr>`;

      const htmlRows = orderedData
        .map((r, idx) => {
          const tds = visibleCols
            .map((c) => {
              const val = r[c.field];
              if (c.field === "netBill") return `<td style="text-align:right">${this.formatCurrency(val)}</td>`;
              if (c.field === "billedAt") return `<td>${this.formatBilledAtIst(val)}</td>`;
              if (c.field === "sxDate") return `<td>${this.formatSxDate(val)}</td>`;
              const safe = (val == null ? "" : String(val)).replace(/</g, "&lt;").replace(/>/g, "&gt;");
              return `<td>${safe}</td>`;
            })
            .join("");
          const rowHtml = `<tr>${tds}</tr>`;
          return idx === firstPastDatedIndex ? `${separatorRow}${rowHtml}` : rowHtml;
        })
        .join("");

      // Build summary table: Billed By x MOP (Cash/Card/UPI/NEFT/Wallet/Other + any extra) and Patient Refund
      const defaultMopOrder = ["Cash", "Card", "UPI", "NEFT", "Wallet", "Other"];
      const foundMops = Array.from(new Set((filteredData || []).map((r) => r.mop).filter((x) => !!x)));
      const orderedMops = defaultMopOrder.filter((m) => foundMops.includes(m));
      const extraMops = foundMops.filter((m) => !defaultMopOrder.includes(m)).sort();
      const mops = [...orderedMops, ...extraMops];

      const billedByNames = Array.from(
        new Set((filteredData || []).map((r) => r.billedBy || "-").filter((x) => !!x))
      ).sort();

      const byBilled = {};
      billedByNames.forEach((name) => {
        byBilled[name] = {
          mopSums: Object.fromEntries(mops.map((m) => [m, 0])),
          all: 0,
          refund: 0,
        };
      });

      (filteredData || []).forEach((r) => {
        const bb = r.billedBy || "-";
        const mop = r.mop || "Other";
        const amountRaw = Number(r.netBill) || 0;
        const amountAbs = Math.abs(amountRaw);
        const remarks = (r.paymentRemarks || "").toLowerCase();
        const typeStr = String(r.type || "").toLowerCase();
        const isRefund = typeStr === "refund";
        const mopKey = mops.includes(mop) ? mop : mops.includes("Other") ? "Other" : mops[0] || "Other";
        if (!byBilled[bb]) {
          byBilled[bb] = {
            mopSums: Object.fromEntries(mops.map((m) => [m, 0])),
            all: 0,
            refund: 0,
          };
        }
        if (isRefund) {
          // subtract refund amount from that MOP's total
          byBilled[bb].mopSums[mopKey] = (byBilled[bb].mopSums[mopKey] || 0) - amountAbs;
          byBilled[bb].refund += amountAbs;
        } else {
          // add payment amount to that MOP's total
          byBilled[bb].mopSums[mopKey] = (byBilled[bb].mopSums[mopKey] || 0) + amountAbs;
        }
        byBilled[bb].all += amountRaw;
      });

      const totalsByCol = {
        mopSums: Object.fromEntries(mops.map((m) => [m, 0])),
        all: 0,
        refund: 0,
      };
      billedByNames.forEach((name) => {
        mops.forEach((m) => {
          totalsByCol.mopSums[m] += byBilled[name].mopSums[m];
        });
        totalsByCol.all += byBilled[name].all;
        totalsByCol.refund += byBilled[name].refund;
      });

      const summaryHeaderCols = mops.map((m) => `<th class="right">${m}</th>`).join("");
      const escapeHtml = (s) => String(s).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const summaryRowsHtml = billedByNames
        .map((name) => {
          const cells = mops
            .map((m) => `<td class="right">${this.formatCurrency(byBilled[name].mopSums[m])}</td>`)
            .join("");
          return `<tr><td>${escapeHtml(name || "-")}</td>${cells}<td class="right">${this.formatCurrency(
            byBilled[name].refund
          )}</td></tr>`;
        })
        .join("");

      const summaryTotalsRow = `
        <tr>
          <th>Total</th>
          ${mops.map((m) => `<th class="right">${this.formatCurrency(totalsByCol.mopSums[m])}</th>`).join("")}
          <th class="right">${this.formatCurrency(totalsByCol.refund)}</th>
        </tr>`;

      const summaryTableHtml = `
        <div style="margin-top:12px">
          <div class="totals">Total Amount: ${this.formatCurrency(total)}</div>
          <table style="margin-top:6px">
            <thead>
              <tr>
                <th>Billed By</th>
                ${summaryHeaderCols}
                <th class="right">Patient Refund</th>
              </tr>
            </thead>
            <tbody>
              ${summaryRowsHtml}
              ${summaryTotalsRow}
            </tbody>
          </table>
        </div>`;

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>MR Bill Register</title>
            <style>
              body { font-family: Arial, Helvetica, sans-serif; color: #111; }
              .header { display:flex; justify-content:space-between; align-items:center; margin-bottom: 12px; }
              .title { font-size: 20px; font-weight: 600; }
              .meta { color: #444; font-size: 12px; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; }
              th, td { border: 1px solid #ccc; padding: 6px 8px; }
              th { background: #efefef; text-align: left; }
              .right { text-align: right; }
              .totals { margin-top: 8px; font-weight: 600; }
              .sx-separator td {
                border-top: 2px solid #111;
                background: #f5f5f5;
                font-weight: 600;
                font-size: 11px;
              }
              @media print { @page { size: A4 landscape; margin: 12mm; } }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="title">MR Bill Register</div>
              <div class="meta">
                Date: ${moment(fromDate).format("DD-MMM-YYYY")} to ${moment(toDate).format("DD-MMM-YYYY")}<br/>
                Branch: ${selectedBranchName} | Doctor: ${displayDoctorName} | MOP: ${
        selectedMop || "All"
      } | Billed By: ${selectedBilledBy || "All"}<br/>
                Report Generated At: ${reportGeneratedAt}
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  ${visibleCols
                    .map((c) => `<th${c.field === "netBill" ? ' class="right"' : ""}>${c.headerName}</th>`)
                    .join("")}
                </tr>
              </thead>
              <tbody>
                ${htmlRows}
              </tbody>
            </table>
            ${summaryTableHtml}
            <script>setTimeout(function(){ window.print(); }, 300);</script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      cogoToast.success("PDF print dialog opened");
    } catch (err) {
      console.error("Error exporting PDF:", err);
      cogoToast.error("Failed to export PDF");
    }
  };

  renderFilters = () => {
    const {
      branches,
      doctors,
      users,
      fromDate,
      toDate,
      selectedBranch,
      selectedDoctor,
      selectedMop,
      selectedBilledBy,
    } = this.state;

    return (
      <Card
        elevation={1}
        style={{
          marginBottom: "16px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
        }}
      >
        <H3 style={{ color: "var(--text-primary)", marginTop: 0 }}>Filters</H3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <ControlGroup>
            <Tag large minimal>
              From Date
            </Tag>
            <DateInput
              formatDate={(d) => moment(d).format("DD/MM/YYYY")}
              parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
              value={fromDate}
              onChange={(d) => this.handleDateChange("fromDate", d)}
              popoverProps={{ minimal: true }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              To Date
            </Tag>
            <DateInput
              formatDate={(d) => moment(d).format("DD/MM/YYYY")}
              parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
              value={toDate}
              onChange={(d) => this.handleDateChange("toDate", d)}
              popoverProps={{ minimal: true }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Branch
            </Tag>
            <div style={{ minWidth: 160 }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Doctors
            </Tag>
            <div style={{ minWidth: 180 }}>
              <SingleSelectWrapper
                items={[`All Doctors (${this.state.doctors.length})`, ...this.createDoctorSelectItems()]}
                selectedItem={this.state.selectedDoctorName || `All Doctors (${this.state.doctors.length})`}
                placeholder="Search doctors..."
                onChange={(value) => {
                  if (value === `All Doctors (${this.state.doctors.length})`) {
                    this.handleDoctorFilterSelect(null);
                  } else {
                    this.handleDoctorFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              M.O.P
            </Tag>
            <HTMLSelect
              options={[
                { label: "All", value: "" },
                { label: "Cash", value: "Cash" },
                { label: "Card", value: "Card" },
                { label: "UPI", value: "UPI" },
                { label: "NEFT", value: "NEFT" },
                { label: "Wallet", value: "Wallet" },
                { label: "Other", value: "Other" },
              ]}
              value={selectedMop}
              onChange={(e) => this.handleFilterChange("selectedMop", e.target.value)}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Billed By
            </Tag>
            <div style={{ minWidth: 240 }}>
              <SingleSelectWrapper
                items={["All", ...users.map((u) => u.name)]}
                selectedItem={selectedBilledBy || "All"}
                placeholder="Search billed by..."
                onChange={(val) => this.handleFilterChange("selectedBilledBy", val === "All" ? "" : val)}
                width="100%"
              />
            </div>
          </ControlGroup>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <Button intent="primary" icon="search" onClick={this.generateReport}>
            Generate
          </Button>
          <Button
            icon="document"
            onClick={this.exportToPDF}
            disabled={!this.state.rows || this.state.rows.length === 0}
            title="Export to PDF"
          >
            PDF
          </Button>
          <Button
            icon="th"
            onClick={this.exportToCSV}
            disabled={!this.state.rows || this.state.rows.length === 0}
            title="Export to CSV"
          >
            CSV
          </Button>
        </div>
      </Card>
    );
  };

  renderHeader = () => {
    const { rows } = this.state;

    const payments = (rows || []).filter((r) => {
      const typeStr = String(r.type || "").toLowerCase();
      return typeStr === "payment";
    });

    const refunds = (rows || []).filter((r) => {
      const typeStr = String(r.type || "").toLowerCase();
      return typeStr === "refund";
    });

    const paymentsTotal = payments.reduce((sum, r) => sum + Math.abs(Number(r.netBill) || 0), 0);
    const refundsTotal = refunds.reduce((sum, r) => sum + Math.abs(Number(r.netBill) || 0), 0);
    const total = paymentsTotal - refundsTotal;
    const pastDatedCount = (rows || []).filter((r) => r.isPastDatedSx).length;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Results</H3>
        <div style={{ color: "var(--text-secondary)" }}>
          <Tag minimal large intent="primary">
            Count: {rows.length}
          </Tag>
          {pastDatedCount > 0 ? (
            <Tag
              minimal
              large
              intent="warning"
              style={{ marginLeft: 8 }}
              title="Payments / refunds recorded against previous-date Surgery (SX) dates. These appear at the bottom of the list."
            >
              Previous-date SX: {pastDatedCount}
            </Tag>
          ) : null}
          <Tag minimal large intent="success" style={{ marginLeft: 8 }}>
            Total Amount: {this.formatCurrency(total)}
          </Tag>
        </div>
      </div>
    );
  };

  renderTable = () => {
    const { rows, isGenerating } = this.state;

    if (isGenerating) {
      return <NonIdealState icon={<Spinner />} title="Generating..." description="Preparing MR Bill Register" />;
    }

    if (!rows || rows.length === 0) {
      return (
        <NonIdealState
          icon={<Icon icon="th" />}
          title="No data"
          description="Try different filters and generate again"
        />
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "600px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={rows}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          animateRows={true}
          enableBrowserTooltips={true}
          getRowStyle={(params) =>
            params.data?.isPastDatedSx
              ? {
                  borderLeft: "3px solid var(--intent-warning, #d9822b)",
                  fontStyle: "italic",
                }
              : undefined
          }
          defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 80 }}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
              },
            ],
            defaultToolPanel: undefined,
          }}
          statusBar={{
            statusPanels: [{ statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" }],
          }}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data.billNo}
          rowHeight={44}
          headerHeight={42}
        />
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon icon="credit-card" style={{ marginRight: "12px", color: "var(--intent-primary)" }} />
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>MR Bill Register</H1>
          </div>
          <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "16px" }}>
            {this.state.selectedBranch
              ? `${this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches"}`
              : "All Branches"}
            {this.state.selectedDoctorName
              ? ` • ${this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)}`
              : ""}{" "}
            - Date range, Branch, Doctors, M.O.P and Billed By filtered MR billing
          </Text>
        </div>

        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderHeader()}
          {this.renderTable()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(MRBillRegister)));
